import { ProspectsFilters } from '../../../../../shared/types/prospects-filters';
import { StorageUtil } from '../../../../../shared/utils/storage';
import { checkIsFilterApplied } from '../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/contact-list-content/helper';
import { getCallTitle } from '../../../../sequence/components/sequence-single-content/components/sequence-single-step-variant-item/helper';
import { SequenceStepType } from '../../../../sequence/enums';
import { CallOutcome, TaskAction, TaskStatusKey } from '../../../utils/enums';
import { SELECT_ALL_PRODUCTS_LIMIT as PROSPECT_SELECT_ALL_PRODUCTS_LIMIT } from '../../../../prospect/helpers/helper';
import { resolveFilters } from '../../../../prospect/helpers/prospect-filters-resolver';

// Re-export for use in tasks
export const SELECT_ALL_PRODUCTS_LIMIT = PROSPECT_SELECT_ALL_PRODUCTS_LIMIT;

export const getCallOutcomeOptions = [
  {
    key: CallOutcome.Interested,
    label: 'Interested',
  },
  {
    key: CallOutcome.CallbackLater,
    label: 'Callback Later',
  },
  {
    key: CallOutcome.Followup,
    label: 'Follow-up',
  },
  {
    key: CallOutcome.NotInterested,
    label: 'Not Interested',
  },
  {
    key: CallOutcome.WrongNumber,
    label: 'Wrong Number',
  },
  {
    key: CallOutcome.NoAnswer,
    label: 'No Answer',
  },
  {
    key: CallOutcome.LeftVoicemail,
    label: 'Left Voicemail',
  },
  {
    key: CallOutcome.NotInService,
    label: 'Not In Service',
  },
];

export const getTaskTitle = (type, purpose = null) => {
  if (type === SequenceStepType.LinkedInConnectionRequest) {
    return 'Connection Request';
  }
  if (type === SequenceStepType.LinkedInMessage) {
    return 'Message';
  }
  if (type === SequenceStepType.LinkedInInMail) {
    return 'InMail';
  }
  if (type === SequenceStepType.LinkedInViewProfile) {
    return 'View Profile';
  }
  if (type === SequenceStepType.LinkedInPostInteraction) {
    return 'Post Interaction';
  }
  if (type === SequenceStepType.Call) {
    return getCallTitle(purpose);
  }
  if (type === SequenceStepType.Introduction) {
    return 'Introduction Call';
  }
  if (type === SequenceStepType.Demo) {
    return 'Demo Call';
  }
  if (type === SequenceStepType.FollowUp) {
    return 'Follow-up Call';
  }
  if (type === SequenceStepType.Reminder) {
    return 'Reminder Call';
  }
  if (type === SequenceStepType.WhatsAppMessage) {
    return 'WhatsApp Message';
  }
  if (type === SequenceStepType.WhatsAppVoiceMessage) {
    return 'WhatsApp Voice Message';
  }
  if (type === SequenceStepType.WhatsAppVoiceCall) {
    return 'WhatsApp Voice Call';
  }
  if (type === SequenceStepType.AimfoxCampaignPush) {
    return 'Push Prospect to Aimfox';
  }
  if (type === SequenceStepType.HeyReachCampaignPush) {
    return 'Push Prospect to HeyReach';
  }

  return 'Custom Task';
};

export const isLinkedInType = (type: SequenceStepType) =>
  type === SequenceStepType.LinkedInConnectionRequest ||
  type === SequenceStepType.LinkedInMessage ||
  type === SequenceStepType.LinkedInPostInteraction ||
  type === SequenceStepType.LinkedInInMail ||
  type === SequenceStepType.LinkedInViewProfile;

export enum TaskSortBy {
  Priority = 'priority',
  CompletedAt = 'completedAt',
  SkippedAt = 'skippedAt',
  DueDate = 'dueDate',
}

export const getDateColumnTitle = (taskStatus: TaskStatusKey) => {
  if (
    taskStatus === TaskStatusKey.DueToday ||
    taskStatus === TaskStatusKey.Upcoming ||
    taskStatus === TaskStatusKey.Overdue
  ) {
    return 'Due Date';
  }
  if (taskStatus === TaskStatusKey.Completed) {
    return 'Completion Date';
  }

  return 'Skipped Date';
};

export const getSortByKeyFromTaskStatus = (
  taskStatus: TaskStatusKey,
): TaskSortBy => {
  switch (taskStatus) {
    case TaskStatusKey.DueToday:
    case TaskStatusKey.Upcoming:
    case TaskStatusKey.Overdue:
      return TaskSortBy.DueDate;
    case TaskStatusKey.Completed:
      return TaskSortBy.CompletedAt;
    case TaskStatusKey.Skipped:
    default:
      return TaskSortBy.SkippedAt;
  }
};

export const removeAgo = (string) => string.replace(/\s*ago\s*/, '');

export const getTaskDate = (taskStatus: TaskStatusKey, cell) => {
  if (
    taskStatus === TaskStatusKey.DueToday ||
    taskStatus === TaskStatusKey.Upcoming ||
    taskStatus === TaskStatusKey.Overdue
  ) {
    return cell?.dueAt;
  }

  if (taskStatus === TaskStatusKey.Completed) {
    return cell?.completedAt;
  }

  return cell?.skippedAt;
};

export const isActionsVisible = (taskStatus: TaskStatusKey) =>
  taskStatus === TaskStatusKey.Completed ||
  taskStatus === TaskStatusKey.Skipped ||
  taskStatus === TaskStatusKey.Failed;

export const getToasterMessage = (taskAction: TaskAction) => {
  if (taskAction === TaskAction.ExecuteTask) {
    return 'Task marked completed successfully.';
  }
  if (taskAction === TaskAction.SkipTask) {
    return 'Task skipped successfully.';
  }
  if (taskAction === TaskAction.RetryLinkedInTask) {
    return 'LinkedIn tasks retried successfully';
  }

  return 'Task snoozed successfully.';
};

export const isCompletedOrSkippedTab = (taskStatus: TaskStatusKey) =>
  taskStatus === TaskStatusKey.Completed ||
  taskStatus === TaskStatusKey.Skipped;

// Tasks Filters
export const setTasksFiltersInLocalStore = (filters: ProspectsFilters) => {
  StorageUtil.setItem('t-f', JSON.stringify(filters));
};
export const getTasksFiltersFromLocalStore = (): ProspectsFilters =>
  JSON.parse(StorageUtil.getItem('t-f'));
export const removeTasksFiltersFromLocalStore = (): void => {
  StorageUtil.removeItem('t-f');
};

export const setTaskTypeFiltersInLocalStore = (taskTypes: number[]) => {
  StorageUtil.setItem('t_t-f', JSON.stringify(taskTypes));
};
export const getTaskTypeFiltersInLocalStore = () =>
  JSON.parse(StorageUtil.getItem('t_t-f'));

export const removeTaskTypeFiltersInLocalStore = () => {
  StorageUtil.removeItem('t_t-f');
};

// Search Filter
export const setSearchFilterInLocalStore = (searchValue: string) => {
  StorageUtil.setItem('t-s', searchValue || '');
};
export const getSearchFilterFromLocalStore = (): string =>
  StorageUtil.getItem('t-s') || '';
export const removeSearchFilterFromLocalStore = (): void => {
  StorageUtil.removeItem('t-s');
};

// Extract ids using reduce method
export const getSelectedTasksId = (selectedTasks) =>
  selectedTasks.reduce((accumulator, currentValue) => {
    accumulator.push(currentValue.id);
    return accumulator;
  }, []);

export const getTaskActionTitle = (taskAction: TaskAction, selectedTaskIds) => {
  if (!selectedTaskIds) {
    return taskAction === TaskAction.ExecuteTask
      ? 'Complete this task'
      : 'Skip task';
  }

  return taskAction === TaskAction.ExecuteTask
    ? `Complete this task(s)`
    : `Skip task`;
};

export const getCallTaskDesc = (selectedTaskIds) =>
  `To mark ${
    selectedTaskIds > 1 ? `${selectedTaskIds} Calling task(s)` : 'calling task'
  } as complete, select call outcome`;

export const getCompleteActionDesc = (selectedTaskIds) =>
  `Are you sure you want to mark ${
    selectedTaskIds > 1 ? `these ${selectedTaskIds} tasks` : 'this task'
  } as complete?`;

export const isCallTask = (type: SequenceStepType) =>
  type === SequenceStepType.Introduction ||
  type === SequenceStepType.Demo ||
  type === SequenceStepType.FollowUp ||
  type === SequenceStepType.Reminder ||
  type === SequenceStepType.Other;

export const isWhatsappTask = (type: SequenceStepType) =>
  type === SequenceStepType.WhatsAppMessage ||
  type === SequenceStepType.WhatsAppVoiceCall ||
  type === SequenceStepType.WhatsAppVoiceMessage;

export const isAnyCallTaskSelected = (selectedTasks) => {
  const callTypes = [
    SequenceStepType.Introduction,
    SequenceStepType.Demo,
    SequenceStepType.FollowUp,
    SequenceStepType.Reminder,
    SequenceStepType.Other,
  ];

  return (
    selectedTasks?.filter((item) => callTypes?.includes(item?.taskTitle?.type))
      ?.length || 0
  );
};

export const getConfirmationModalClass = (
  taskAction: TaskAction,
  taskType: SequenceStepType,
  selectedTasks,
) => {
  if (
    taskAction === TaskAction.ExecuteTask &&
    isAnyCallTaskSelected(selectedTasks)
  ) {
    return 'task-confirmation-modal-tertiary';
  }

  return taskAction === TaskAction.ExecuteTask && isCallTask(taskType)
    ? 'task-confirmation-modal-call'
    : 'task-confirmation-modal-second';
};

export const getTaskTableClass = (tasksFilters, selectedTasks) => {
  if (checkIsFilterApplied(tasksFilters) && selectedTasks) {
    return 'lg-table';
  }

  if (checkIsFilterApplied(tasksFilters)) {
    return 'sm-table';
  }

  if (selectedTasks) {
    return 'md-table';
  }

  return '';
};

export const getBulkActionRequestPayload = (
  bulkSelectedTasks,
  selectedTasks = [],
) => {
  const { deselectedTaskIds, tasksFilter } = bulkSelectedTasks;

  // For bulk selection, we use tasksFilter + deSelectedContactIds + taskIds
  // For individual selection, we use taskIds
  if (bulkSelectedTasks?.isAllTasksSelected) {
    return {
      ...(deselectedTaskIds?.length > 0 && { deselectedTaskIds }),
      ...(tasksFilter && { tasksFilter }),
    };
  }
  return {
    ...(deselectedTaskIds?.length > 0 && { deselectedTaskIds }),
    ...(tasksFilter && { tasksFilter }),
    taskIds: selectedTasks.map((task) => task.id),
  };
};

export const formatNumberWithCommas = (num: number): string => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }
  return num?.toLocaleString('en-US');
};

/**
 * Filters selected tasks based on current filter criteria
 * @param selectedTasks - Array of selected tasks
 * @param currentFilters - Current applied filters
 * @param currentTasks - Current tasks data that matches the filter
 * @returns Filtered array of selected tasks that match current criteria
 */
export const filterSelectedTasksByCurrentCriteria = (
  selectedTasks: any[],
  currentFilters: any,
  currentTasks: any[],
): any[] => {
  if (!selectedTasks || selectedTasks.length === 0) {
    return [];
  }

  // Get the IDs of tasks that match current filter criteria
  const currentTaskIds = new Set(currentTasks.map((task) => task.id));

  // Filter selected tasks to only include those that exist in current filtered results
  return selectedTasks.filter((task) => currentTaskIds.has(task.id));
};

// Helper functions for EmailSelectedRowInfoBanner
export const getShouldShowSelectedRowInfoBanner = (
  selectedTasks: any[],
  bulkSelectedTasks: any,
  isBulkTasksSelected: boolean,
) =>
  selectedTasks.length > 0 ||
  bulkSelectedTasks?.isAllTasksSelected ||
  isBulkTasksSelected;

export const getSelectedTasksCount = (
  selectedTasks: any[],
  bulkSelectedTasks: any,
  totalTasks: number,
  currentFilters?: any,
  currentTasks?: any[],
) => {
  if (bulkSelectedTasks?.isAllTasksSelected) {
    const count =
      totalTasks > SELECT_ALL_PRODUCTS_LIMIT
        ? SELECT_ALL_PRODUCTS_LIMIT
        : totalTasks;

    return count - bulkSelectedTasks?.deselectedTaskIds?.length;
  }

  // If filters are applied, filter the selected tasks to only include those that match current criteria
  if (currentFilters && currentTasks && selectedTasks.length > 0) {
    const filteredSelectedTasks = filterSelectedTasksByCurrentCriteria(
      selectedTasks,
      currentFilters,
      currentTasks,
    );
    return filteredSelectedTasks.length;
  }

  return selectedTasks.length;
};

export const getFormattedTasksCount = (selectedTasksCount: number) =>
  `${selectedTasksCount
    ?.toString()
    ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} Task(s) selected.`;

// Helper to build status count params from local storage
export const buildTasksStatusCountParamsFromLocalStorage = (
  sortKey?: string,
  sortOrder?: string,
) => {
  const localStorageFilters = getTasksFiltersFromLocalStore();
  const filtersToUseLocal = localStorageFilters
    ? (resolveFilters(localStorageFilters) as any)
    : {};
  const searchFromLocal = getSearchFilterFromLocalStore();
  const taskTypeFromLocal = getTaskTypeFiltersInLocalStore();
  return {
    ...(filtersToUseLocal?.sequences && {
      sequenceIds: filtersToUseLocal?.sequences,
    }),
    ...(filtersToUseLocal?.owners && { owners: filtersToUseLocal?.owners }),
    ...(filtersToUseLocal?.['created date'] && {
      dueDate: filtersToUseLocal?.['created date'],
    }),
    ...(filtersToUseLocal?.outcomes && {
      outcomes: filtersToUseLocal?.outcomes,
    }),
    ...(filtersToUseLocal?.callOutcomes && {
      callOutcomes: filtersToUseLocal?.callOutcomes,
    }),
    ...(filtersToUseLocal?.['task priority'] && {
      priority: filtersToUseLocal?.['task priority'],
    }),
    ...(searchFromLocal && { search: searchFromLocal }),
    ...(taskTypeFromLocal &&
      taskTypeFromLocal.length > 0 && {
        taskType: taskTypeFromLocal.join(','),
      }),
    ...(sortKey && { sortBy: sortKey }),
    ...(sortOrder && { sortOrder: sortOrder.toUpperCase() }),
    ...(filtersToUseLocal?.subSequenceIds && {
      subSequenceId: filtersToUseLocal?.subSequenceIds,
    }),
  };
};
